import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Box, Typography, Skeleton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../redux/reducers/fieldEngagementReducer';
import {
    CustomReactSelect,
    USAMapBubbleChart,
    HighchartBarChart,
    CustomButtonGroup,
    USACountryChart,
} from '../../../shared';
import {
    DISTRIBUTION_CHART_OPTION,
    USA_CHART_OPTIONS,
    hideDistrictMap,
    mapOptions,
} from './constants';
import {
    REZLIDHIA_COMPANY_ID,
    BAVENCIO_COMPANY_ID,
    FIELD_ENGAGEMENT_STATUS,
    BREAKDOWN,
} from '../../../../constants';

const MemoizedUSAMapBubbleChart = React.memo(USAMapBubbleChart);

const BreakDowns = {
    66: ['Region - RMD', 'Territory-IROC', 'State'],
    140: ['Region - RMD', 'Territory-IROC', 'State'],
    161: ['Region - RMD', 'Territory-IROC', 'State'],
    default: BREAKDOWN,
};

function DistrictBreakdown() {
    const { company } = useSelector((state) => state.company);
    const finalBreakDowns = useMemo(
        () => BreakDowns[company.id] || BreakDowns.default,
        [company.id],
    );
    const [engagementStatus, setEngagementStatus] = useState({
        label: 'All',
        value: undefined,
    });
    const [engagementSegment, setEngagementSegment] = useState({
        label: 'All',
        value: undefined,
    });
    const [selectedBreakdown, setSelectedBreakdown] = useState(
        finalBreakDowns[0],
    );
    const dispatch = useDispatch();
    const { regionByStatusAndSegment, resetStates } = actions;
    const {
        distributionRegion,
        stateDistributionRegion,
        stateBreakdownUSAMapData,
        districtBreakdownUSAMapData,
        overallPhysicians,
        fieldPhysicianSegments,
        isLoading,
        stateMapSeries,
        territoriesBreakdownUSAMapData,
        distributionTerritories,
        overallTerritoriesPhysicians,
        distributionRegionSegment,
        stateBreakdownUSAMapSegmentData,
    } = useSelector((state) => state.fieldEngagement);
    const [barChartSeries, setBarChartSeries] = useState(distributionRegion);
    const finalLoading = isLoading;
    useMemo(() => {
        if (company.id && hideDistrictMap.includes(company.id)) {
            setSelectedBreakdown(finalBreakDowns[0]);
        } else {
            setSelectedBreakdown(finalBreakDowns[0]);
        }
    }, [company.id]);
    const handleEngagementSegmentChange = (option) => {
        dispatch(resetStates());
        const segment = option.label === 'All' ? undefined : option.label,
            status = engagementStatus.value;

        setEngagementSegment(option);

        dispatch(
            regionByStatusAndSegment({
                segment: segment,
                status: status,
            }),
        );
    };

    const handleEngagementStatusChange = (option) => {
        dispatch(resetStates());
        const segment =
                engagementSegment.label === 'All'
                    ? undefined
                    : engagementSegment.label,
            status = option.value;

        setEngagementStatus(option);

        dispatch(
            regionByStatusAndSegment({
                segment: segment,
                status: status,
            }),
        );
    };

    const handleDistrictBreakdown = (option) => {
        setSelectedBreakdown(option);
    };

    const customisation = useMemo(() => {
        const companyId = company.id;
        let chartSeries = [],
            breakdown = '',
            count = 0,
            bubbleMapData = [];

        if (companyId && companyId === REZLIDHIA_COMPANY_ID) {
            chartSeries = distributionRegionSegment;
            breakdown = 'District / State';
            count = overallPhysicians;
            bubbleMapData = stateBreakdownUSAMapSegmentData;
        } else if (companyId && hideDistrictMap.includes(companyId)) {
            chartSeries = stateDistributionRegion;
            breakdown = selectedBreakdown;
            count = overallPhysicians;
            bubbleMapData = districtBreakdownUSAMapData;
        } else if (selectedBreakdown === 'State') {
            chartSeries = stateDistributionRegion;
            breakdown = selectedBreakdown;
            count = overallPhysicians;
        } else if (selectedBreakdown === 'Territory-IROC') {
            chartSeries = distributionTerritories;
            breakdown = selectedBreakdown;
            count = overallTerritoriesPhysicians;
            bubbleMapData = territoriesBreakdownUSAMapData;
        } else {
            chartSeries = distributionRegion;
            breakdown = selectedBreakdown;
            count = overallPhysicians;
            bubbleMapData = districtBreakdownUSAMapData;
        }

        if (companyId === BAVENCIO_COMPANY_ID) {
            chartSeries = chartSeries?.filter(
                ([label]) => label?.toLowerCase() !== 'unassigned'
            );
        }

        return {
            chartSeries,
            breakdown,
            count,
            bubbleMapData,
        };
    }, [
        company.id,
        selectedBreakdown,
        distributionRegion,
        stateDistributionRegion,
    ]);

    useEffect(() => {
        setBarChartSeries(customisation.chartSeries);
    }, [distributionRegion, selectedBreakdown]);
    useEffect(() => {
        if (company.id) {
            setEngagementStatus({ label: 'All', value: undefined });
            setEngagementSegment({ label: 'All', value: undefined });
            setSelectedBreakdown(
                hideDistrictMap.includes(company.id)
                    ? finalBreakDowns[1]
                    : finalBreakDowns[0],
            );
        }
    }, [company.id]);

    return (
        <Box className="box-sizing-border-box padding-24px">
            <Box className="padding-tb-20 border-bottom-gray flex-row-space-between-center">
                <Box>
                    <Typography className="text-lg-semi-bold gray-900">
                        {customisation.breakdown} Breakdown of Field Physician
                        Engagement
                    </Typography>
                </Box>
                {!(
                    company.id === REZLIDHIA_COMPANY_ID ||
                    hideDistrictMap.includes(company.id)
                ) ? (
                    <Box>
                        <CustomButtonGroup
                            data={finalBreakDowns}
                            handleClick={(option) =>
                                handleDistrictBreakdown(option)
                            }
                            key="breakdown"
                            selected={selectedBreakdown}
                        />
                    </Box>
                ) : null}
            </Box>
            <Grid container className="flex-row-center">
                <Grid item className="us-bubble-map-container" lg={7} xl={8}>
                    {/* Location Chart */}
                    {finalLoading ? (
                        <Skeleton
                            height={400}
                            variant="rectangular"
                            width="95%"
                        />
                    ) : ['Territory-IROC', 'Region - RMD', 'District'].includes(
                          selectedBreakdown,
                      ) ? (
                        <MemoizedUSAMapBubbleChart
                            chartOptions={mapOptions}
                            key={`${company.id}-${selectedBreakdown}-${overallPhysicians}`}
                            seriesData={
                                overallPhysicians > 0
                                    ? company.id === REZLIDHIA_COMPANY_ID
                                        ? stateBreakdownUSAMapData
                                        : customisation.bubbleMapData
                                    : []
                            }
                        />
                    ) : (
                        <USACountryChart
                            chartOptions={USA_CHART_OPTIONS}
                            mapSeries={stateMapSeries}
                        />
                    )}
                </Grid>
                <Grid item lg={5} xl={4}>
                    <Box className="container-p-24-br-16 bg-gray-50 mt-24px">
                        {/* Location Info */}
                        <Box className="flex-column gap-4">
                            <Typography className="text-lg-semi-bold gray-900">
                                {customisation.breakdown} Breakdown of Field
                                Physician Engagement
                            </Typography>
                            <Typography
                                className="text-sm-regular gray-500"
                                key={`${company.id}-${selectedBreakdown}-${overallPhysicians}`}
                            >
                                {finalLoading ? (
                                    <Skeleton variant="text" width={200} />
                                ) : (
                                    overallPhysicians
                                )}{' '}
                                Overall Physicians
                            </Typography>
                        </Box>
                        {company.id === REZLIDHIA_COMPANY_ID && finalLoading ? (
                            <Box className="flex-row-space-between pt-12px">
                                <Skeleton
                                    height={60}
                                    variant="text"
                                    width={150}
                                />
                                <Skeleton
                                    height={60}
                                    variant="text"
                                    width={150}
                                />
                            </Box>
                        ) : company.id === REZLIDHIA_COMPANY_ID ? (
                            <Box className="flex-row-space-between pt-12px">
                                <Box className="">
                                    <CustomReactSelect
                                        handleFilter={
                                            handleEngagementStatusChange
                                        }
                                        label="Engagement Status"
                                        options={FIELD_ENGAGEMENT_STATUS}
                                        placeholder="Select Engagement Status"
                                        selectedOption={engagementStatus}
                                    />
                                </Box>
                                <Box className="">
                                    <CustomReactSelect
                                        handleFilter={
                                            handleEngagementSegmentChange
                                        }
                                        label="Engagement Segment"
                                        options={fieldPhysicianSegments}
                                        placeholder="Select Engagement Segment"
                                        selectedOption={engagementSegment}
                                    />
                                </Box>
                            </Box>
                        ) : null}
                        <Box className="pt-24px district-breakdown-bar-chart">
                            {finalLoading ? (
                                <Skeleton
                                    height={400}
                                    variant="rectangular"
                                    width="100%"
                                />
                            ) : (
                                <HighchartBarChart
                                    chartOptions={{
                                        ...DISTRIBUTION_CHART_OPTION,
                                        chart: {
                                            ...DISTRIBUTION_CHART_OPTION.chart,
                                            height:
                                                barChartSeries?.length > 0
                                                    ? barChartSeries?.length *
                                                      35
                                                    : 0,
                                        },
                                    }}
                                    key={barChartSeries}
                                    seriesData={barChartSeries}
                                    seriesName="Field Engagement"
                                />
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default DistrictBreakdown;
